import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { styled } from '@mui/material/styles'
import { DemoDisabledAction } from './layout/DemoDisabledAction'

// マイタスクの成果物タブ・ウォッチャータブは、デモでは開けないようにする
// （タスクタブだけを見せる方針）。タブ自体は本物と同じく3つ並べたままにして、
// 押したときにヘッダーの無効化ボタン群と同じ案内（DemoDisabledAction）を出す。
//
// タブは製品コード（view/pages/MyWbsItems/index.tsx）の中でMUIのTabsとして
// 描画されるため、呼び出し側から<DemoDisabledAction>で包むことができない。
// そこで、描画済みのタブ要素の上に同じ見た目・同じ挙動のオーバーレイを
// ポータルで重ね、クリックをそちらで受け止める。製品コードにデモ用の分岐や
// propsを足さずに済ませるための構成。
//
// 対象タブのidは製品コード側のallyProps（`simple-tab-${index}`）が付ける。
// MUIのクラス名やDOMの並び順ではなくこのidで引くのは、そちらが実装詳細で
// 変わりうるのに対し、idはaria-controlsとの対応で固定されているため。
const DISABLED_TAB_IDS = ['simple-tab-1', 'simple-tab-2']

type OverlayRect = {
  tabId: string
  top: number
  left: number
  width: number
  height: number
}

// タブに重ねる透明な当たり判定。
//
// 位置決めをDemoDisabledActionの外側でやるのが要点。DemoDisabledActionは
// childrenを素の<span>（display: inline）で包み、TooltipはそのDOMを基準に
// Popperを出す。内側だけをfixedで飛ばすと、アンカーになる<span>はbody先頭で
// 潰れたままなので、Popperがタブではなく画面の隅に出てしまう。
// ここで実寸の箱を作り、その中の<span>を box いっぱいに広げることで、
// Tooltipが見るアンカー矩形をタブそのものに一致させる。
const OverlayBox = styled('div')({
  position: 'fixed',
  // 画面側が作るスタッキングコンテキスト（PageAreaのFade等）に埋もれないよう、
  // ページ内容より十分上に置く。ただしMUIのダイアログ（既定1300）より
  // 手前には出さない。単票ダイアログを開いている間にタブの当たり判定が
  // 前面へ出てくるのを防ぐため。
  zIndex: 1200,
  display: 'flex',
  // DemoDisabledActionの<span>はinline要素で、そのままでは中身の高さしか
  // 持たない。flexアイテムとして縦横いっぱいに伸ばす。
  '& > span': {
    display: 'block',
    flex: 1,
    // カーソルはタブ本来の見た目（MUIのTabはButtonBaseベースでpointer）の
    // ままにする。DemoDisabledActionはnot-allowedをインラインstyleで付けるため、
    // !importantで打ち消す。ヘッダーのボタン群（同じコンポーネントを使う）の
    // 見え方は変えたくないので、DemoDisabledAction側ではなくここで上書きする。
    cursor: 'pointer !important',
  },
})

// タブの位置はウィンドウ幅やフォント読み込みで変わるので、実DOMから都度測る。
const measure = (): OverlayRect[] =>
  DISABLED_TAB_IDS.flatMap(tabId => {
    const element = document.getElementById(tabId)
    if (!element) return []
    const { top, left, width, height } = element.getBoundingClientRect()
    return [{ tabId, top, left, width, height }]
  })

export const DemoDisabledMyTasksTabs = () => {
  const [rects, setRects] = useState<OverlayRect[]>([])
  // 測り直しの結果が前回と同じならsetStateしない。ResizeObserverは
  // レイアウトが動かない変化でも発火しうるため、毎回新しい配列を入れると
  // 再描画が続いてしまう。
  const prevKeyRef = useRef<string>('')

  // 測定はフレームに1回へ束ねる。DOMの監視はAgGridの再描画でも発火するため、
  // 呼ばれるたびにgetBoundingClientRectを叩くとレイアウトを強制することになり、
  // グリッドのスクロールが重くなる。
  const frameRef = useRef<number | undefined>(undefined)
  const remeasure = useCallback(() => {
    if (frameRef.current !== undefined) return
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = undefined
      const next = measure()
      const key = JSON.stringify(next)
      if (key === prevKeyRef.current) return
      prevKeyRef.current = key
      setRects(next)
    })
  }, [])

  // オーバーレイが受けるのはポインタ操作だけなので、キーボード操作
  // （タブへフォーカスを移してEnter/Space）は素通りしてしまう。MUIのTabsは
  // selectionFollowsFocus未指定＝矢印キーはフォーカス移動のみで、確定は
  // Enter/Spaceのため、その2キーをキャプチャ段階で止める。
  useEffect(() => {
    const blockKeyboardActivation = (event: KeyboardEvent) => {
      if (event.key !== 'Enter' && event.key !== ' ') return
      const target = event.target
      if (!(target instanceof HTMLElement)) return
      if (!DISABLED_TAB_IDS.includes(target.id)) return
      event.preventDefault()
      event.stopPropagation()
    }
    document.addEventListener('keydown', blockKeyboardActivation, true)
    return () =>
      document.removeEventListener('keydown', blockKeyboardActivation, true)
  }, [])

  useEffect(() => {
    // タブはマウント直後には存在しない。MyWbsItemsは表示状態（UIState）を
    // 読み終えるまで<Loading>だけを返すため、初回のremeasureは必ず空振りする。
    // DOMの出現を待って測り直さないとオーバーレイが一度も作られず、
    // タブがそのまま押せてしまう。
    //
    // ResizeObserverはタブ自身のサイズ変化しか拾えず、
    // （デモ画面はoverflow: hiddenで高さが固定のため）bodyの監視も発火しない。
    // 出現・位置ずれの両方を拾える MutationObserver で追う。
    const resizeObserver = new ResizeObserver(remeasure)
    const observeTabs = () => {
      resizeObserver.disconnect()
      DISABLED_TAB_IDS.forEach(tabId => {
        const element = document.getElementById(tabId)
        if (element) resizeObserver.observe(element)
      })
    }

    const onDomChanged = () => {
      observeTabs()
      remeasure()
    }

    const mutationObserver = new MutationObserver(onDomChanged)
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      // タブ行の位置はヘッダーの検索条件の折り返し等でも動くため、
      // style/classの変化も拾う。
      attributes: true,
      attributeFilter: ['style', 'class'],
    })

    onDomChanged()
    window.addEventListener('resize', remeasure)
    return () => {
      window.removeEventListener('resize', remeasure)
      mutationObserver.disconnect()
      resizeObserver.disconnect()
      if (frameRef.current !== undefined) {
        cancelAnimationFrame(frameRef.current)
      }
    }
  }, [remeasure])

  if (rects.length === 0) return <></>

  return createPortal(
    <>
      {rects.map(rect => (
        <OverlayBox
          key={rect.tabId}
          style={{
            top: rect.top,
            left: rect.left,
            width: rect.width,
            height: rect.height,
          }}
        >
          {/* disableClick: 案内はホバー時のPopperだけに寄せ、クリックでは
              Snackbarを出さない。タブは画面内に常時見えている要素なので、
              押すたびに通知が積み上がると邪魔になる。クリック自体は
              DemoDisabledActionのspanが止めるため、タブは開かない。 */}
          <DemoDisabledAction disableClick={true}>
            <div style={{ width: '100%', height: '100%' }} />
          </DemoDisabledAction>
        </OverlayBox>
      ))}
    </>,
    document.body
  )
}
